import React, { useState, useEffect, useRef } from 'react';
import { memberAPI } from '../../services/api';
import { cn } from '../../lib/utils';

/**
 * MemberPartnerSearch - Searchable dropdown for selecting playing partners
 * Mobile-first design with touch-friendly interactions
 */
const MemberPartnerSearch = ({
  selectedPartners = [],
  onPartnersChange,
  maxPartners = 3,
  excludeGhin = null,
  className = ''
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchTimer = setTimeout(async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await memberAPI.searchPartners(query);
        let members = response.data.members || [];

        // Filter out already selected partners and self
        members = members.filter(m => {
          if (excludeGhin && m.ghin === excludeGhin) return false;
          if (selectedPartners.some(p => p.ghin === m.ghin)) return false;
          return true;
        });

        setResults(members);
      } catch (err) {
        console.error('Partner search error:', err);
        setError('Failed to search members');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [query, excludeGhin, selectedPartners]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleSelectPartner = (member) => {
    if (selectedPartners.length >= maxPartners) {
      setError(`Maximum ${maxPartners} partners allowed`);
      return;
    }

    const newPartner = {
      ghin: member.ghin,
      firstName: member.firstName,
      lastName: member.lastName,
      displayName: member.displayName || `${member.firstName} ${member.lastName}`,
      relationship: 'preferred'
    };

    onPartnersChange([...selectedPartners, newPartner]);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setError('');
  };

  const handleRemovePartner = (ghin) => {
    onPartnersChange(selectedPartners.filter(p => p.ghin !== ghin));
    setError('');
  };

  const handleRelationshipChange = (ghin, relationship) => {
    onPartnersChange(
      selectedPartners.map(p =>
        p.ghin === ghin ? { ...p, relationship } : p
      )
    );
  };

  const relationshipOptions = [
    { value: 'preferred', label: 'Preferred Partner' },
    { value: 'friend', label: 'Friend' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'business', label: 'Business Associate' },
    { value: 'regular-partner', label: 'Regular Partner' }
  ];

  return (
    <div className={cn('space-y-3', className)} ref={dropdownRef}>
      {/* Selected Partners */}
      {selectedPartners.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Selected Partners ({selectedPartners.length}/{maxPartners})
          </label>
          <div className="space-y-2">
            {selectedPartners.map((partner) => (
              <div
                key={partner.ghin}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex-1">
                  <span className="font-medium text-green-800">
                    {partner.displayName || `${partner.firstName} ${partner.lastName}`}
                  </span>
                  <span className="text-xs text-green-600 ml-2">
                    GHIN: {partner.ghin}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={partner.relationship}
                    onChange={(e) => handleRelationshipChange(partner.ghin, e.target.value)}
                    className="text-sm border border-green-300 rounded-lg px-3 py-2 bg-white min-h-[44px]"
                  >
                    {relationshipOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleRemovePartner(partner.ghin)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                    aria-label="Remove partner"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      {selectedPartners.length < maxPartners && (
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search for a partner
          </label>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => query.length >= 2 && setIsOpen(true)}
              placeholder="Type name or GHIN..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              autoComplete="off"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-5 w-5 border-2 border-green-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>

          {/* Search Results Dropdown */}
          {isOpen && query.length >= 2 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {results.length > 0 ? (
                results.map((member) => (
                  <button
                    key={member.ghin}
                    type="button"
                    onClick={() => handleSelectPartner(member)}
                    className="w-full px-4 py-3 text-left hover:bg-green-50 focus:bg-green-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-gray-900">
                      {member.displayName || `${member.firstName} ${member.lastName}`}
                    </div>
                    <div className="text-sm text-gray-500 flex gap-3">
                      <span>GHIN: {member.ghin}</span>
                      {member.handicapIndex !== undefined && (
                        <span>HI: {member.handicapIndex}</span>
                      )}
                    </div>
                  </button>
                ))
              ) : !isLoading ? (
                <div className="px-4 py-3 text-gray-500 text-center">
                  No members found matching "{query}"
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Helper Text */}
      {selectedPartners.length === 0 && (
        <p className="text-sm text-gray-500">
          Search by name or GHIN number to find partners you'd like to play with.
        </p>
      )}
    </div>
  );
};

export default MemberPartnerSearch;
